import { Suspense, lazy } from "react";
import "./pages.scss";
import { Route, Routes } from "react-router-dom";
import { connect } from "react-redux";
import { withMemo } from "helper/global";
import { navigation } from "./navigation";
import { granted } from "helper/access";
import Layout from "./layout/layout";

const Home = lazy(() => import("./home/home.js"));
const CatalogClassification = lazy(() => import("./catalog/classification/classification"));
const CatalogRating = lazy(() => import("./catalog/rating/rating"));
const CatalogField = lazy(() => import("./catalog/field/field"));
const CatalogActivity = lazy(() => import("./catalog/activity/activity"));
const CatalogCategory = lazy(() => import("./catalog/category/category"));
const CatalogProject = lazy(() => import("./catalog/project/project"));
const DataRegister = lazy(() => import("./data/register/register"));
const DataStrategy = lazy(() => import("./data/strategy/strategy-group"));
const DataCost = lazy(() => import("./data/cost/cost-group"));
const DataGepSpatial = lazy(() => import("./data/geo/geo"));
const DataFile = lazy(() => import("./data/file/file"));
const TaskManage = lazy(() => import("./analysis/manage/manage"));
const TaskPlan = lazy(() => import("./analysis/plan/plan"));
const FinanceLookup = lazy(() => import("./finance/lookup/lookup"));
const FinanceInput = lazy(() => import("./finance/input/input"));
const FinanceOutput = lazy(() => import("./finance/output/output"));
const ToolsBudget = lazy(() => import("./tools/budget/budget"));
const ToolsProject = lazy(() => import("./tools/project/project"));
const ToolsSmoothing = lazy(() => import("./tools/smoothing/smoothing"));
const ReportQuery = lazy(() => import("./report/query/query"));
const ReportQaQc = lazy(() => import("./report/qaqc/qaqc"));
const ConnectInput = lazy(() => import("./connect/input/input"));
const ConnectOutput = lazy(() => import("./connect/output/output"));
const Collect = lazy(() => import("./collect/user"));
const AdminAccess = lazy(() => import("./admin/access/access"));
const AdminSettings = lazy(() => import("./admin/setting/setting"));
const AdminAudit = lazy(() => import("./admin/audit/audit"));
const Apps = lazy(() => import("./apps/apps"));
const Ticket = lazy(() => import("pages/ticket/ticket"));
const UserSettings = lazy(() => import("./layout/settings/user-setting"));

function Component(props) {
  //
  // constants

  // functions

  function setNavigation() {
    const u = props.user;
    const nav = JSON.parse(JSON.stringify(navigation));
    const menu = (t) => nav.find((i) => i.text === t);
    const page = (m, t) => m.items.find((i) => i.text === t);
    const anyVisible = (m) => m.items.some((i) => i.visible);

    // Catalog — single gate
    menu("Catalog").visible = granted("Catalog", "Read");

    // Data — per-page gates
    const data = menu("Data");
    page(data, "Asset").visible = granted("Asset", "Read");
    page(data, "Strategy").visible = granted("Strategy", "Read");
    page(data, "Cost").visible = granted("Cost", "Read");
    page(data, "Geospatial").visible = granted("Gis", "Read");
    page(data, "File").visible = granted("File", "Read");
    data.visible = anyVisible(data);

    // Analysis — per-page gates
    const analysis = menu("Analysis");
    page(analysis, "Manage").visible = granted("Manage", "Read");
    page(analysis, "Plan").visible = granted("Plan", "Read");
    analysis.visible = anyVisible(analysis);

    // Tools — single gate covering Budget, Project and Smoothing
    menu("Tools").visible = granted("Tools", "Read");

    // Finance — single gate
    menu("Finance").visible = granted("Finance", "Read");

    // Report — per-page gates
    const report = menu("Report");
    page(report, "Query").visible = granted("Report", "Read");
    page(report, "QaQc").visible = granted("QaQc", "Read");
    report.visible = anyVisible(report);

    // Konnect / Kollect — single gate
    menu("Konnect").visible = granted("Connect", "Read");
    menu("Kollect").visible = granted("Collect", "Read");

    // Admin — per-page gates (organization sits under Access on the Admin gate)
    const admin = menu("Admin");
    page(admin, "Access").visible = granted("Access", "Read") || u.typeId === 1;
    page(admin, "Settings").visible = granted("Settings", "Read") || granted("Display", "Read") || granted("Gis", "Read");
    page(admin, "Audit Logs").visible = granted("AuditLog", "Read");
    admin.visible = anyVisible(admin);

    if (props.app.length > 0) {
      var items = props.app.map((i) => {
        return { text: i.description, path: `/app/${i.name}`, icon: i.icon };
      });
      nav.push({
        text: "Apps",
        icon: "fad fa-rocket-launch",
        items: items,
      });
    }
    nav.forEach((i) => {
      i.text = i.text;
      if (i.items) i.items.forEach((j) => (j.text = j.text));
    });
    return nav;
  }

  // render

  return <MemorizedLayout setNavigation={setNavigation} />;
}

function mapStateToProps(state) {
  return {
    user: state.user,
    app: state.app,
  };
}

export default connect(mapStateToProps, null)(Component);

const MemorizedLayout = withMemo((props) => {
  return (
    <Layout navigation={props.setNavigation()}>
      <Suspense fallback={<div />}>
      <Routes>
        <Route path="/pages" element={<Home />} />
        <Route path="/pages/home" element={<Home />} />
        <Route path="/pages/catalog/*" element={<CatalogClassification />} />
        <Route path="/pages/catalog/classification/*" element={<CatalogClassification />} />
        <Route path="/pages/catalog/rating/*" element={<CatalogRating />} />
        <Route path="/pages/catalog/field/*" element={<CatalogField />} />
        <Route path="/pages/catalog/activity/*" element={<CatalogActivity />} />
        <Route path="/pages/catalog/category/*" element={<CatalogCategory />} />
        <Route path="/pages/catalog/project/*" element={<CatalogProject />} />

        <Route path="/pages/data/*" element={<DataRegister />} />
        <Route path="/pages/data/register/*" element={<DataRegister />} />
        <Route path="/pages/data/strategy/*" element={<DataStrategy />} />
        <Route path="/pages/data/cost/*" element={<DataCost />} />
        <Route path="/pages/data/geo/*" element={<DataGepSpatial />} />
        <Route path="/pages/data/file/*" element={<DataFile />} />

        <Route path="/pages/analysis/*" element={<TaskManage />} />
        <Route path="/pages/analysis/manage/*" element={<TaskManage />} />
        <Route path="/pages/analysis/plan/*" element={<TaskPlan />} />

        <Route path="/pages/finance/*" element={<FinanceLookup />} />
        <Route path="/pages/finance/lookup/*" element={<FinanceLookup />} />
        <Route path="/pages/finance/input/*" element={<FinanceInput />} />
        <Route path="/pages/finance/output/*" element={<FinanceOutput />} />

        <Route path="/pages/tools/*" element={<ToolsBudget />} />
        <Route path="/pages/tools/budget/*" element={<ToolsBudget />} />
        <Route path="/pages/tools/project/*" element={<ToolsProject />} />
        <Route path="/pages/tools/smoothing/*" element={<ToolsSmoothing />} />

        <Route path="/pages/report/*" element={<ReportQuery />} />
        <Route path="/pages/report/query/*" element={<ReportQuery />} />
        <Route path="/pages/report/qaqc/*" element={<ReportQaQc />} />

        <Route path="/pages/connect/*" element={<ConnectInput />} />
        <Route path="/pages/connect/input/*" element={<ConnectInput />} />
        <Route path="/pages/connect/output/*" element={<ConnectOutput />} />

        <Route path="/pages/collect" element={<Collect />} />

        <Route path="/pages/admin/*" element={<AdminAccess />} />
        <Route path="/pages/admin/access/*" element={<AdminAccess />} />
        <Route path="/pages/admin/setting/*" element={<AdminSettings />} />
        <Route path="/pages/admin/audit/*" element={<AdminAudit />} />

        <Route path="/pages/user/setting/*" element={<UserSettings />} />
        <Route path="/pages/app/*" element={<Apps />} />
        <Route path="/pages/ticket/*" element={<Ticket />} />
      </Routes>
      </Suspense>
    </Layout>
  );
}, []);
